import { z } from '../zod.js';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';

export const CANCEL_PAYMENT_REFUND_REASONS = [
  'duplicate',
  'fraudulent',
  'requested_by_customer',
] as const;

export const CancelPaymentParamsValidationSchema = z.object({
  paymentId: ObjectIdStringValidationSchema.meta({
    description:
      'The ID of the payment to cancel. Bookings for event pages have their own IDs and cannot be canceled here.',
  }),
});

const CancelPaymentRefundValidationSchema = z
  .object({
    amount: z.number().int().positive().meta({
      description:
        'Amount to refund in the smallest currency unit (for example, `2500` for $25.00). Cannot be more than the amount still refundable on the payment.',
    }),
    reason: z.enum(CANCEL_PAYMENT_REFUND_REASONS).meta({
      description: `Why the payment is refunded. Valid values: ${CANCEL_PAYMENT_REFUND_REASONS.join(', ')}.`,
    }),
    note: z.string().max(500).optional().meta({
      description: 'Free-text note stored with the refund.',
    }),
    sendEmail: z.boolean().optional().meta({
      description:
        'When `true`, emails the customer a refund receipt. Defaults to `false`. When `sendCancellationEmail` is also `true`, the customer gets one cancellation email that includes the refund instead.',
    }),
  })
  .strict()
  .meta({
    description:
      'Also refund the customer in the same request. The refund runs first: if it fails, the payment is not canceled. Omit it to cancel without refunding.',
  });

export const CancelPaymentRequestBodyValidationSchema = z
  .object({
    reason: z.string().trim().min(1).max(500).meta({
      description:
        "Why the payment is being canceled. Stored on the payment and returned as `canceledReason`. Canceling is final: it ends the whole order, order bumps included, puts the stock back and removes the buyer's access to the order's files and license key.",
    }),
    sendCancellationEmail: z.boolean().optional().meta({
      description:
        'When `true`, emails the customer that their order has been canceled. Defaults to `false`.',
    }),
    refund: CancelPaymentRefundValidationSchema.optional(),
  })
  .strict()
  .meta({
    description:
      "Cancels a one-time payment. A cancel is final and cannot be undone. It ends the whole order, order bumps included, puts the product, variant and order bump stock back, and removes the buyer's access to the order's files and license key. It does not refund unless `refund` is given. Revenue totals stay gross: a cancel does not lower them. A payment that isn't complete yet (status `created` or `draft`), that didn't go through (`failed`, `canceled` or `draft_expired`) or that is already canceled returns 400.",
  });
